import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { withRouter } from 'react-router-dom';
import { Grid, Tooltip, IconButton } from '@mui/material';
import {
    BreadCrumbs,
    LoadingBar,
    AddButton,
    PageTitleWithToolTip,
    DataGrid,
    BulkModal,
} from '../Common';
import {
    StyledCellLink,
    BadgeWithLinkedText,
    Percentage,
    FractionPercentage,
    Weight,
} from '../Common/DataGrid/CellComponents';
import {
    getDateRangeFilterOperator,
    getRangeFilterOperator
} from '../Common/DataGrid/FilterComponents';
import {
    dataGridContainerProps,
    text_commonCellProps,
    select_commonCellProps,
    getLocalStorageKey,
} from '../Common/DataGrid/utils';
import { fetchFilterValues, postRequestHeaders, defaultToastOptions } from '../../utils';
import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { TranslateW } from "../../Utils/Languages/Translate";
import { useBreadCrumbs, useBulkUpdates } from '../../hooks';
import { isDesktop } from 'react-device-detect';

const batchUpdateType = "serviceEvents";
const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});
const enabledFieldOptions = [{ value: "true", label: "Enabled" }, { value: "false", label: "Disabled" }]

const ServiceEvents = (props) => {
    const {
        history = {},
        history: {
            push = () => { },
        } = {}
    } = props;
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const [batchUpdateModelOpen, setBatchUpdateModelOpen] = useState(false);
    const sortByDescendingID = "id_desc";

    const { isLoading, BatchUpdateExport, BatchUpdateImport } = useBulkUpdates();

    useBreadCrumbs({
        path: { id: 'ServiceEvents', text: `Service Events`, link: history.location.pathname, reset: true }
    });

    const fetchFilters = async () => {
        const filters = await fetchFilterValues(Routes.WorkItem.TableFilters());
        setFilters(filters);
        setLoading(false);
    };
    
    const submitDataGridEdit = async (unsavedRows) => {
        const mappedData = unsavedRows.map(wi => ({ id: wi.id, enabled: wi.enabled }));
        const response = await fetch(Routes.WorkItem.DataGridEdit(), {
            method: "POST",
            headers: { ...postRequestHeaders },
            body: JSON.stringify(mappedData),
        });
        
        if (response.status === 200) {
            return toast.success(
                "Service Events successfully updated.", 
                { ...defaultToastOptions }
            );
        }
        
        return toast.error(
            "Something went wrong. Service Events could not be saved.", 
            { ...defaultToastOptions }
        );
    };

    useEffect(() => {
        fetchFilters();
        
        const key = "serviceEventTableCacheCleared";

        const savedTableState = JSON.parse(localStorage.getItem(getLocalStorageKey(
            Routes.WorkItem.WorkItemTable(sortByDescendingID)
        )));
        const serviceEventTableCacheCleared = localStorage.getItem(key);

        if (savedTableState && !serviceEventTableCacheCleared) {
            localStorage.removeItem(getLocalStorageKey(
                Routes.WorkItem.WorkItemTable(sortByDescendingID)
            ));
            localStorage.setItem(key, "true");
            window.location.reload();
        }
    }, []);

    const columns = [
        {
            field: 'idString',
            headerName: 'Service Event ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workItemStatus, id, idString } }) => (
                <BadgeWithLinkedText
                    statusProperty={workItemStatus}
                    text={idString}
                    to={{
                        pathname: `/ServiceEvent/${id}`,
                        state: {
                            from: window.location.pathname
                        }
                    }}
                />
            )
        },
        {
            field: 'region',
            headerName: 'Region',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'locationId',
            headerName: 'Location',
            ...select_commonCellProps,
            // US 16741: Location hyperlink (secondary/underlink) enabled at all breakpoints (was desktop-only).
            renderCell: ({ row: { locationId, locationName } }) => (
                <StyledCellLink
                    path={`/Location/${locationId}`}
                    text={locationName}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'locationCode',
            headerName: 'Location Code',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'city',
            headerName: 'City',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'locationState',
            headerName: 'State',
            ...select_commonCellProps
        },
        {
            field: 'workOrderId',
            headerName: 'Work Order ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workOrderId } }) => (
                <StyledCellLink
                    path={`/WorkOrder/${workOrderId}`}
                    text={workOrderId}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'workOrderNumber',
            headerName: 'Work Order Number',
            ...text_commonCellProps,
        },
        {
            field: 'referenceNumber',
            headerName: 'Reference Number',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'workItemType',
            headerName: 'Type',
            ...select_commonCellProps
        },
        {
            field: 'serviceDate',
            headerName: 'Service Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'workItemStatus',
            headerName: 'Status',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'primaryTechnicianName',
            headerName: 'Primary Technician',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
            ...select_commonCellProps,
            renderCell: ({ row: { assetName, assetId } }) => (
                <StyledCellLink
                    path={`/Inventory/${assetId}`}
                    text={assetName}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'servicerName',
            headerName: 'Service Provider',
            ...select_commonCellProps
        },
        {
            field: 'netGasLbsAdded',
            headerName: 'Net Gas Added',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { netGasLbsAdded } }) => <Weight value={netGasLbsAdded} />
        },
        {
            field: 'leakRatio',
            headerName: 'Per Occurance Leak Rate %',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
            renderCell: ({ row: { leakRatio } }) => <Percentage value={leakRatio} />
        },
        {
            // AB#14852: EPA Rolling Average leak rate as of the SE date, per-page from the DWH.
            // Not sortable/filterable: the value is enriched onto the returned page only.
            field: 'epaRollingLeakRate',
            headerName: 'Leak Rate - EPA Rolling Avg (%)',
            flex: 1,
            initiallyVisible: false,
            sortable: false,
            filterable: false,
            renderCell: ({ row: { epaRollingLeakRate } }) => <FractionPercentage value={epaRollingLeakRate} />
        },
        {
            // AB#16788: true trailing-365-day rolling average as of the SE date, per-page from the DWH.
            // Not sortable/filterable: the value is enriched onto the returned page only.
            field: 'twelveMonthRollingLeakRate',
            headerName: '12-Month Average Leak Rate (%)',
            flex: 1,
            initiallyVisible: false,
            sortable: false,
            filterable: false,
            renderCell: ({ row: { twelveMonthRollingLeakRate } }) => <FractionPercentage value={twelveMonthRollingLeakRate} />
        },
        {
            // AB#16849: highest (peak) EPA leak rate over the trailing 12 months, from the
            // non-resetting DWH fact table (AB#15322 semantics) — same value on every row of the asset.
            field: 'highestEpaLeakRateLast12Months',
            headerName: 'Highest EPA Leak Rate - Last 12 Months',
            flex: 1,
            initiallyVisible: false,
            sortable: false,
            filterable: false,
            renderCell: ({ row: { highestEpaLeakRateLast12Months } }) => <FractionPercentage value={highestEpaLeakRateLast12Months} />
        },
        {
            // AB#16849: highest (peak) EPA leak rate since Jan 1 of the current calendar year.
            field: 'highestEpaLeakRateCalendarYear',
            headerName: 'Highest EPA Leak Rate - Calendar Year',
            flex: 1,
            initiallyVisible: false,
            sortable: false,
            filterable: false,
            renderCell: ({ row: { highestEpaLeakRateCalendarYear } }) => <FractionPercentage value={highestEpaLeakRateCalendarYear} />
        },
        {
            field: 'r3Filled',
            headerName: 'R3 Filled',
            initiallyVisible: false,
            ...select_commonCellProps,
            renderCell: ({ row: { r3Filled } }) => r3Filled ? "Yes" : "No"
        },
        {
            field: 'systemManager',
            headerName: 'System Manager',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'classification',
            headerName: 'Classification',
            initiallyVisible: false,
            ...text_commonCellProps,
        }
    ];
    
    const editViewColumns = [
        {
            id: 1,
            field: 'enabled',
            headerName: 'Status',
            editable: true,
            ...select_commonCellProps,
            valueOptions: enabledFieldOptions,
            renderCell: ({ row: { enabled } }) => (enabled === true || enabled === "true") ? "Enabled" : "Disabled",
            valueSetter: (value, row) => ({ ...row, enabled: value })
        },
        {
            field: 'idString',
            headerName: 'Service Event ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workItemStatus, id, idString } }) => (
                <BadgeWithLinkedText
                    statusProperty={workItemStatus}
                    text={idString}
                    to={{
                        pathname: `/ServiceEvent/${id}`,
                        state: {
                            from: window.location.pathname
                        }
                    }}
                />
            )
        },
        {
            field: 'workOrderId',
            headerName: 'Work Order ID',
            ...text_commonCellProps,
            renderCell: ({ row: { workOrderId } }) => (
                <StyledCellLink
                    path={`/WorkOrder/${workOrderId}`}
                    text={workOrderId}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'workItemType',
            headerName: 'Type',
            ...select_commonCellProps
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
            ...select_commonCellProps,
            renderCell: ({ row: { assetName, assetId } }) => (
                <StyledCellLink
                    path={`/Inventory/${assetId}`}
                    text={assetName}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            renderCell: ({ row: { locationId, locationName } }) => (
                <StyledCellLink
                    path={`/Location/${locationId}`}
                    text={locationName}
                    from={"Service Events"}
                />
            )
        },
        {
            field: 'serviceDate',
            headerName: 'Service Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'primaryTechnicianName',
            headerName: 'Primary Technician',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'createdByName',
            headerName: 'Created By',
            ...text_commonCellProps
        },
    ];

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                sx={{ display: { xs: 'none', lg: 'flex' } }}
                path={[
                    { text: `Service Events` }
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} sx={{ px: { xs: 2, md: 0 }, mt: { xs: 4, md: 0 } }}>
                    <PageTitleWithToolTip
                        pageTitle={TranslateW("serviceActivityView.service-events")}
                        toolTipText={TranslateW("ServiceEventsDescription")}
                    />
                </Grid>

                {UserInfo.isSystemAdministrator() && (
                    <>
                        <Grid item sx={{ marginLeft: "auto", display: { xs: 'none', lg: 'inline-flex' } }}>
                            <Tooltip title="Export Service Events Template">
                                <IconButton
                                    onClick={(_) => BatchUpdateExport(
                                        batchUpdateType,
                                        { sort: null, filters: [] },
                                    )}
                                >
                                    <img
                                        src="/images/batchUpdateExport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item sx={{ marginRight: "1%", display: { xs: 'none', lg: 'inline-flex' } }}>
                            <Tooltip title="Bulk Import Service Events">
                                <IconButton
                                    onClick={_ => setBatchUpdateModelOpen(true)}
                                >
                                    <img
                                        src="/images/batchUpdateImport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </>
                )}

                {(!UserInfo.isReadOnly()) && (
                    <Grid
                        item
                        align="right"
                        sx={{
                            ...(!UserInfo.isSystemAdministrator() && { marginLeft: 'auto' }),
                            display: { xs: 'none', lg: 'block' }
                        }}
                    >
                        <AddButton
                            text={TranslateW("createWorkOrderServiceEvent")}
                            onClick={() => push("/CreateWorkOrder", { from: 'Service Events' })}
                        />
                    </Grid>
                )}

                <Grid item { ...dataGridContainerProps }>
                    <DataGrid
                        url={Routes.WorkItem.WorkItemTable(sortByDescendingID)}
                        filterOptions={filters}
                        exportButtonURL={Routes.ServiceEvents.TableExport()}
                        exportButtonAlt={TranslateW('ExportServiceEvents')}
                        exportFileName={"ServiceEvents"}
                        printButtonURL={Routes.ServiceEvents.TableExport()}
                        printButtonAlt={TranslateW('PrintResultsToPdf')}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Create a new Service Event or change your search criterias."
                        // The props below will need to be reworked to use the new data grid
                        searchKeyword={history.location.state === undefined ? "" : history.location.state.searchKeyword}
                        searchById={history.location.state === undefined ? "" : history.location.state.searchById}
                        isOrCondition={history.location.state === undefined ? false : history.location.state.isOrCondition}
                        columns={columns}
                        exportFileTypes={[{ type: 'csv', name: 'CSV' }, { type: 'pdf', name: 'PDF' }]}
                        renderEditButton={isDesktop && UserInfo.isSystemAdministrator()}
                        editOnClick={submitDataGridEdit}
                        editViewColumns={editViewColumns}
                    />
                </Grid>
            </Grid>

            <BulkModal
                open={batchUpdateModelOpen}
                onClose={() => setBatchUpdateModelOpen(false)}
                onClick={async (excelFile) => {
                    await BatchUpdateImport(batchUpdateType, excelFile);
                    setBatchUpdateModelOpen(false);
                }}
                loading={isLoading}
            />
        </>
    );
};

export default withRouter(ServiceEvents);
